import React, { useState, useEffect } from 'react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { publicAPI } from '../services/api';
import { ArrowLeft, Building2, Users, BookOpen, ChevronRight, Search, Loader, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const BrowseSchools = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    try {
      setLoading(true);
      const response = await publicAPI.getSchools();
      const schoolsData = response.data?.schools || response.data || [];
      setSchools(Array.isArray(schoolsData) ? schoolsData : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching schools:', err);
      setError('Failed to load schools. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filteredSchools = schools.filter(school => 
    school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    school.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSchoolInitials = (name) => {
    return name.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 3);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Header />
      
      <div className="container mx-auto px-4 py-12">
        {/* Back button */}
        <Link to="/" className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 mb-8">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Home</span>
        </Link>
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Browse All Schools</h1>
            <p className="text-gray-600">Select a school to access department-specific learning materials</p>
          </div>
          
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search schools..."
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 animate-pulse">
            {[1, 2, 3].map(i => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-6 border-b border-gray-100 last:border-0 items-center">
                <div className="md:col-span-5 flex items-center gap-4">
                  <div className="w-14 h-14 bg-gray-200 rounded-xl"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3.5 bg-gray-150 rounded w-1/2"></div>
                  </div>
                </div>
                <div className="md:col-span-2 text-center">
                  <div className="h-6 bg-gray-200 rounded w-8 mx-auto mb-1"></div>
                  <div className="h-3 bg-gray-100 rounded w-12 mx-auto"></div>
                </div>
                <div className="md:col-span-2 text-center">
                  <div className="h-6 bg-gray-200 rounded w-8 mx-auto mb-1"></div>
                  <div className="h-3 bg-gray-100 rounded w-12 mx-auto"></div>
                </div>
                <div className="md:col-span-2 text-center">
                  <div className="h-6 bg-gray-200 rounded w-8 mx-auto mb-1"></div>
                  <div className="h-3 bg-gray-100 rounded w-12 mx-auto"></div>
                </div>
                <div className="md:col-span-1 h-6 w-6 bg-gray-200 rounded-full mx-auto hidden md:block"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-red-800 mb-2">{error}</h3>
            <button 
              onClick={fetchSchools}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : filteredSchools.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-12 text-center">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No schools found</h3>
            <p className="text-gray-600">Try adjusting your search term.</p>
          </div>
        ) : (
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100"
          >
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-12 gap-4 p-6 border-b border-gray-200 bg-gray-50">
              <div className="col-span-5 font-medium text-gray-700">School Name</div>
              <div className="col-span-2 font-medium text-gray-700 text-center">Departments</div>
              <div className="col-span-2 font-medium text-gray-700 text-center">Materials</div>
              <div className="col-span-2 font-medium text-gray-700 text-center">Teachers</div>
              <div className="col-span-1"></div>
            </div>
            
            {/* Schools List */}
            {filteredSchools.map((school) => (
              <motion.div 
                key={school._id || school.id} 
                variants={itemVariants}
                className="group border-b border-gray-100 last:border-0"
              >
                <Link to={`/school/${school._id || school.id}`}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 p-6 hover:bg-blue-50 transition-colors">
                    {/* School Name */}
                    <div className="md:col-span-5 flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center font-bold text-white shadow-md bg-gradient-to-br from-blue-500 to-indigo-600`}>
                        {getSchoolInitials(school.name)}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors text-lg">
                          {school.name}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-1 max-w-xs">
                          {school.description || `Explore ${school.name} resources`}
                        </p>
                      </div>
                    </div>
                    
                    {/* Mobile visible stats */}
                    <div className="grid grid-cols-3 md:contents gap-2 mt-4 md:mt-0">
                      {/* Departments */}
                      <div className="md:col-span-2 flex flex-col md:flex-row items-center justify-center gap-1 md:gap-0">
                        <div className="text-center">
                          <div className="text-xl md:text-2xl font-bold text-gray-900">
                            {Array.isArray(school.departments) 
                              ? school.departments.length 
                              : (typeof school.departments === 'number' 
                                  ? school.departments 
                                  : (school.departmentsCount || school.totalDepartments || 0))}
                          </div>
                          <div className="text-xs md:text-sm text-gray-500">Departments</div>
                        </div>
                      </div>
                      
                      {/* Materials */}
                      <div className="md:col-span-2 flex flex-col md:flex-row items-center justify-center gap-1 md:gap-0">
                        <div className="text-center">
                          <div className="text-xl md:text-2xl font-bold text-gray-900">
                            {school.materialsCount ?? school.materials ?? school.totalMaterials ?? 0}
                          </div>
                          <div className="text-xs md:text-sm text-gray-500 flex items-center justify-center gap-1">
                            <BookOpen className="w-3 h-3 md:hidden" />
                            Materials
                          </div>
                        </div>
                      </div>
                      
                      {/* Teachers */}
                      <div className="md:col-span-2 flex flex-col md:flex-row items-center justify-center gap-1 md:gap-0">
                        <div className="text-center">
                          <div className="text-xl md:text-2xl font-bold text-gray-900">
                            {school.teachersCount ?? school.teachers ?? school.totalTeachers ?? 0}
                          </div>
                          <div className="text-xs md:text-sm text-gray-500 flex items-center justify-center gap-1">
                            <Users className="w-3 h-3 md:hidden" />
                            Teachers
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action */}
                    <div className="hidden md:col-span-1 md:flex items-center justify-end">
                      <ChevronRight className="w-6 h-6 text-gray-400 group-hover:text-blue-600 transform group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
        
        {/* Stats Summary */}
        {!loading && !error && filteredSchools.length > 0 && (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-8 text-white shadow-lg">
              <div className="text-4xl font-bold mb-2">
                {schools.length}
              </div>
              <div className="text-blue-100 font-medium opacity-90">Total Schools</div>
              <p className="text-blue-50/70 text-sm mt-2">Verified university institutions</p>
            </div>
            
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-8 text-white shadow-lg">
              <div className="text-4xl font-bold mb-2">
                {schools.reduce((sum, s) => {
                  const depts = Array.isArray(s.departments) 
                    ? s.departments.length 
                    : (typeof s.departments === 'number' 
                        ? s.departments 
                        : (s.departmentsCount || s.totalDepartments || 0));
                  return sum + depts;
                }, 0)}
              </div>
              <div className="text-indigo-100 font-medium opacity-90">Total Departments</div>
              <p className="text-indigo-50/70 text-sm mt-2">Across all school faculties</p>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 text-white shadow-lg">
              <div className="text-4xl font-bold mb-2">
                {schools.reduce((sum, s) => sum + (s.materialsCount ?? s.materials ?? s.totalMaterials ?? 0), 0)}
              </div>
              <div className="text-emerald-100 font-medium opacity-90">Learning Materials</div>
              <p className="text-emerald-50/70 text-sm mt-2">Available for student download</p>
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default BrowseSchools;